import React from 'react';
import { Database, Edit, Lock, Unlock, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { useAuth } from '../../../contexts/AuthContext';
import { ROLES } from '../../../utils/rbac';
import { UnifiedModuleBlocks } from './UnifiedModuleBlocks';
import './ModuleShell.css';

const STATUS_COLORS = {
  not_started: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  draft: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  in_progress: { bg: 'rgba(74, 90, 58, 0.12)', color: '#4A5A3A' },
  needs_review: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  completed: { bg: 'rgba(90, 122, 58, 0.12)', color: '#5A7A3A' },
  locked: { bg: 'rgba(74, 74, 74, 0.12)', color: '#4A4A4A' },
};

const STATUS_LABELS = {
  not_started: 'Not Started',
  draft: 'Draft',
  in_progress: 'In Progress',
  needs_review: 'Needs Review',
  completed: 'Completed',
  locked: 'Locked',
};

export function ModuleShell({ 
  report,
  activeModule,
  moduleList = [],
  moduleBlocks = [],
  onUpdateBlocks,
  currentUser,
  onBackToExtraction,
  onPreviousModule,
  onNextModule,
  onContinueToAssembled,
  onSaveModule,
  onLockModule,
  onStatusTransition,
  onCompleteModule,
  onCompleteAllModules,
  onApproveAllBlocks,
  canProceedToNextModule = true,
  canProceedToDrafting = true
}) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canSave = [ROLES.ADMIN, ROLES.REVIEWER].includes(user?.role);
  const canLock = user?.role === ROLES.ADMIN || user?.role === ROLES.SUPERADMIN;
  
  const module = moduleList.find(m => m.id === activeModule);
  const moduleData = report?.modules?.find(m => m.id === activeModule);
  const status = moduleData?.status || 'not_started';
  const statusStyle = STATUS_COLORS[status] || STATUS_COLORS.not_started;
  const statusLabel = t(`reports.status.${status}`, { defaultValue: STATUS_LABELS[status] || status });
  const isLocked = status === 'locked';
  const canComplete = canSave && status !== 'locked' && status !== 'completed';
  const hasUnapprovedBlocks = Array.isArray(moduleBlocks)
    && moduleBlocks.some(block => block?.status !== 'approved');
  const canApproveAll = canSave && !isLocked && hasUnapprovedBlocks && typeof onApproveAllBlocks === 'function';
  const allModulesCompleted = Array.isArray(report?.modules)
    ? report.modules.every(module => module.status === 'completed' || module.status === 'locked')
    : false;
  const moduleName = module?.nameKey
    ? t(module.nameKey, { defaultValue: module?.fallbackName || '' })
    : module?.fallbackName || '';
  const moduleDescription = module?.descriptionKey
    ? t(module.descriptionKey, { defaultValue: module?.fallbackDescription || '' })
    : module?.fallbackDescription || '';
  
  const currentIndex = moduleList.findIndex(m => m.id === activeModule);
  const isFirstModule = currentIndex === 0;
  const isLastModule = currentIndex === moduleList.length - 1;
  const showContinueToAssembled = isLastModule && typeof onContinueToAssembled === 'function';
  const canGoBackToExtraction = typeof onBackToExtraction === 'function';
  const isApprovalBlocked = showContinueToAssembled
    ? !canProceedToDrafting
    : !canProceedToNextModule;
  const approvalHint = isApprovalBlocked
    ? showContinueToAssembled
      ? t('reports.workspace.approvalGate.drafting')
      : t('reports.workspace.approvalGate.nextModule')
    : null;
  
  // Mock provenance badges
  const provenanceBadges = [
    { type: 'station', label: t('reports.workspace.provenance.stationData'), icon: Database },
    { type: 'manual', label: t('reports.workspace.provenance.manualEntry'), icon: Edit },
  ];

  return (
    <div className="module-shell">
      {/* Module Header */}
      <div className="module-shell__header">
        <div className="module-shell__header-content">
          <h2 className="module-shell__header-title">
            {t('reports.workspace.moduleHeading', { number: module?.number, name: moduleName })}
          </h2>
          <p className="module-shell__header-description">
            {moduleDescription}
          </p>
          
          <div className="module-shell__header-badges">
            {provenanceBadges.map((badge, idx) => {
              const Icon = badge.icon;
              return (
                <span key={idx} className="module-shell__header-badge">
                  <Icon size={12} />
                  {badge.label}
                </span>
              );
            })}
          </div>
          
          <div className="module-shell__header-status-row">
            <div className="module-shell__header-status-left">
              <span
                className="module-shell__header-status-badge"
                style={{
                  background: statusStyle.bg,
                  color: statusStyle.color,
                }}
              >
                {statusLabel}
              </span>
              {isLocked && (
                <span className="module-shell__header-lock-indicator">
                  <Lock size={14} />
                  {t('reports.workspace.lockedBy', {
                    name: moduleData?.lockedBy || t('reports.workspace.fallback.admin'),
                  })}
                </span>
              )}
            </div>
            {canLock && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onLockModule(activeModule)}
                className="module-shell__header-lock-btn"
                icon={isLocked ? <Unlock size={14} /> : <Lock size={14} />}
              >
                {isLocked ? t('reports.workspace.actions.unlock') : t('reports.workspace.actions.lock')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="module-shell__content">
        <UnifiedModuleBlocks
          blocks={moduleBlocks}
          moduleId={activeModule}
          onUpdateBlocks={onUpdateBlocks}
          currentUser={currentUser}
        />
      </div>

      {/* Module Footer */}
      <div className="module-shell__footer">
        <div className="module-shell__footer-left">
          {canGoBackToExtraction && (
            <Button
              variant="primary"
              onClick={onBackToExtraction}
              className="module-shell__footer-nav-btn"
            >
              {t('common.back')}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={onPreviousModule}
            disabled={isFirstModule}
            className="module-shell__footer-nav-btn"
            icon={<ChevronLeft size={16} />}
          >
            {t('reports.workspace.actions.previousModule')}
          </Button>
        </div>
        
        <div className="module-shell__footer-right">
          {canSave && status !== 'locked' && (
            <Button
              variant="secondary"
              onClick={() => onSaveModule(activeModule)}
              className="module-shell__footer-save-btn"
              icon={<Save size={16} />}
            >
              {t('reports.workspace.actions.saveModule')}
            </Button>
          )}

          {canComplete && typeof onCompleteModule === 'function' && (
            <Button
              variant="secondary"
              onClick={() => onCompleteModule(activeModule)}
              className="module-shell__footer-status-btn"
            >
              {t('reports.workspace.actions.markAsCompleted')}
            </Button>
          )}

          {canApproveAll && (
            <Button
              variant="secondary"
              onClick={() => onApproveAllBlocks(activeModule)}
              className="module-shell__footer-status-btn"
            >
              {t('reports.workspace.actions.approveAll')}
            </Button>
          )}

          {canSave && !allModulesCompleted && typeof onCompleteAllModules === 'function' && (
            <Button
              variant="ghost"
              onClick={onCompleteAllModules}
              className="module-shell__footer-status-btn"
            >
              {t('reports.workspace.actions.markAllCompleted')}
            </Button>
          )}
          
          {status === 'needs_review' && canSave && (
            <Button
              variant="ghost"
              onClick={() => onStatusTransition('draft')}
              className="module-shell__footer-status-btn"
            >
              {t('reports.workspace.actions.markAsDraft')}
            </Button>
          )}
          
          {status === 'draft' && canSave && (
            <Button
              variant="secondary"
              onClick={() => onStatusTransition('needs_review')}
              className="module-shell__footer-status-btn"
            >
              {t('reports.workspace.actions.requestReview')}
            </Button>
          )}
          
          {status === 'needs_review' && canLock && (
            <Button
              variant="ghost"
              onClick={() => onStatusTransition('locked')}
              className="module-shell__footer-status-btn"
            >
              {t('reports.workspace.actions.lockModule')}
            </Button>
          )}
          
          {showContinueToAssembled ? (
            <Button
              variant="primary"
              onClick={onContinueToAssembled}
              disabled={!canProceedToDrafting}
              className="module-shell__footer-nav-btn"
              icon={<ChevronRight size={16} />}
              iconPosition="right"
            >
              {t('reports.workspace.actions.continueToDrafting')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={onNextModule}
              disabled={isLastModule || !canProceedToNextModule}
              className="module-shell__footer-nav-btn"
              icon={<ChevronRight size={16} />}
              iconPosition="right"
            >
              {t('reports.workspace.actions.nextModule')}
            </Button>
          )}
        </div>
      </div>
      {approvalHint && (
        <div className="module-shell__footer-hint" role="status" aria-live="polite">
          {approvalHint}
        </div>
      )}
    </div>
  );
}

